import Dexie from 'dexie';
import type {SessionOperation} from '../Auth/tabSession';

/** Convert the existing account store in place. A failed or stale conversion rolls
 * back, and concurrent IndexedDB writers cannot interleave with its read/write. */
export async function upgradeAccountStore(
    kind: 'Backups' | 'Autosave',
    operation: SessionOperation,
    convert: (key: string, value: unknown) => unknown,
): Promise<void> {
    operation.assertCurrent();
    const {firmId, userId} = operation.identity;
    const name = `${kind}_${firmId}_${userId}`;
    const exists = await Dexie.exists(name);
    operation.assertCurrent();
    if (!exists) return;

    const db = new Dexie(name);
    try {
        await db.open();
        operation.assertCurrent();
        if (!db.tables.some(table => table.name === name)) return;
        const table = db.table(name);
        await db.transaction('rw', table, async () => {
            operation.assertCurrent();
            const keys = await table.toCollection().primaryKeys();
            for (const key of keys) {
                operation.assertCurrent();
                const value = await table.get(key);
                operation.assertCurrent();
                const converted = convert(String(key), value);
                operation.assertCurrent();
                if (converted !== value) await table.put(converted, key);
            }
            operation.assertCurrent();
        });
        operation.assertCurrent();
    } finally {
        db.close();
    }
}
